<template>
  <div ref="voltageChartRootRef">
    <div class="border border-slate-200 bg-white p-4 shadow-sm">
      <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 class="text-base font-semibold text-slate-800">{{ t('sensor.console.history_voltage') }}</h3>
        <div class="flex flex-wrap items-center gap-2">
          <div class="flex items-center gap-3">
            <label class="flex items-center gap-1.5">
              <button
                type="button"
                role="switch"
                :aria-checked="showLinePhase"
                :aria-label="t('sensor.console.voltage_chart.phase_neutral')"
                :title="t('sensor.console.voltage_chart.phase_neutral')"
                :disabled="showLinePhase && !showPhasePhase"
                class="relative inline-flex h-5 w-9 items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
                :class="showLinePhase ? 'border-blue-600 bg-blue-600' : 'border-slate-300 bg-slate-200'"
                @click="toggleLinePhase">
                <span
                  class="inline-block h-3.5 w-3.5 bg-white shadow transition-transform"
                  :class="showLinePhase ? 'translate-x-[1.125rem]' : 'translate-x-0.5'" />
              </button>
              <span class="text-xs font-medium text-slate-600">{{ t('sensor.console.voltage_chart.phase_neutral') }}</span>
            </label>
            <label class="flex items-center gap-1.5">
              <button
                type="button"
                role="switch"
                :aria-checked="showPhasePhase"
                :aria-label="t('sensor.console.voltage_chart.phase_phase')"
                :title="t('sensor.console.voltage_chart.phase_phase')"
                :disabled="showPhasePhase && !showLinePhase"
                class="relative inline-flex h-5 w-9 items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
                :class="showPhasePhase ? 'border-blue-600 bg-blue-600' : 'border-slate-300 bg-slate-200'"
                @click="togglePhasePhase">
                <span
                  class="inline-block h-3.5 w-3.5 bg-white shadow transition-transform"
                  :class="showPhasePhase ? 'translate-x-[1.125rem]' : 'translate-x-0.5'" />
              </button>
              <span class="text-xs font-medium text-slate-600">{{ t('sensor.console.voltage_chart.phase_phase') }}</span>
            </label>
          </div>
          <TimeRangeFilter default-preset="24h" @change="onRangeChange" />
          <button
            type="button"
            class="flex items-center gap-1.5 rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-700 transition-colors hover:border-slate-400 hover:bg-slate-50"
            :title="t('sensor.console.voltage_chart.maximize_title')"
            @click="openFullscreen">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
            {{ t('sensor.console.voltage_chart.maximize') }}
          </button>
        </div>
      </div>

      <p v-if="fixedPhasesLabel" class="mb-3 flex items-start gap-2 border border-yellow-300 bg-yellow-50/70 px-3 py-2 text-xs text-yellow-800">
        <svg xmlns="http://www.w3.org/2000/svg" class="mt-0.5 h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span>{{ t('sensor.console.voltage_chart.fixed_phase_hint', { phases: fixedPhasesLabel }) }}</span>
      </p>

      <p v-if="showPhasePhaseMissingHint" class="mb-3 flex items-start gap-2 border border-yellow-300 bg-yellow-50/70 px-3 py-2 text-xs text-yellow-800">
        <svg xmlns="http://www.w3.org/2000/svg" class="mt-0.5 h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span>{{ t('sensor.console.voltage_chart.phase_phase_missing_hint') }}</span>
      </p>

      <SensorChartAlarmCards
        v-model:expanded="alarmsSectionExpanded"
        :alarms="alarmList"
        :is-loading="isAlarmsLoading"
        accent="red"
        @select-alarm="openAlarmDetail"
        @hover-alarm="onAlarmHover" />

      <div ref="chartContainer" class="h-96 min-h-[24rem] w-full" />
      <p v-if="isLoading && !hasAnyChartData" class="mt-2 text-center text-sm text-slate-500">
        {{ t('sensor.console.voltage_chart.loading') }}
      </p>
      <p v-else-if="!isLoading && hasTriggeredLoad && !hasAnyChartData" class="mt-2 text-center text-sm text-slate-500">
        {{ t('sensor.console.voltage_chart.no_data') }}
      </p>
    </div>

    <!-- Overlay tela cheia: mesmos filtros + gráfico -->
    <Teleport to="body">
      <Transition name="fade">
        <div
          v-if="isFullscreen"
          class="fixed inset-0 z-[100000] flex flex-col bg-white"
          role="dialog"
          aria-modal="true"
          :aria-label="t('sensor.console.voltage_chart.fullscreen_aria')">
          <div class="flex flex-none flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-4 py-3">
            <h3 class="text-lg font-semibold text-slate-800">{{ t('sensor.console.history_voltage') }}</h3>
            <div class="flex flex-wrap items-center gap-2">
              <div class="flex items-center gap-3">
                <label class="flex items-center gap-1.5">
                  <button
                    type="button"
                    role="switch"
                    :aria-checked="showLinePhase"
                    :aria-label="t('sensor.console.voltage_chart.phase_neutral')"
                    :title="t('sensor.console.voltage_chart.phase_neutral')"
                    :disabled="showLinePhase && !showPhasePhase"
                    class="relative inline-flex h-5 w-9 items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
                    :class="showLinePhase ? 'border-blue-600 bg-blue-600' : 'border-slate-300 bg-slate-200'"
                    @click="toggleLinePhase">
                    <span
                      class="inline-block h-3.5 w-3.5 bg-white shadow transition-transform"
                      :class="showLinePhase ? 'translate-x-[1.125rem]' : 'translate-x-0.5'" />
                  </button>
                  <span class="text-xs font-medium text-slate-600">{{ t('sensor.console.voltage_chart.phase_neutral') }}</span>
                </label>
                <label class="flex items-center gap-1.5">
                  <button
                    type="button"
                    role="switch"
                    :aria-checked="showPhasePhase"
                    :aria-label="t('sensor.console.voltage_chart.phase_phase')"
                    :title="t('sensor.console.voltage_chart.phase_phase')"
                    :disabled="showPhasePhase && !showLinePhase"
                    class="relative inline-flex h-5 w-9 items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
                    :class="showPhasePhase ? 'border-blue-600 bg-blue-600' : 'border-slate-300 bg-slate-200'"
                    @click="togglePhasePhase">
                    <span
                      class="inline-block h-3.5 w-3.5 bg-white shadow transition-transform"
                      :class="showPhasePhase ? 'translate-x-[1.125rem]' : 'translate-x-0.5'" />
                  </button>
                  <span class="text-xs font-medium text-slate-600">{{ t('sensor.console.voltage_chart.phase_phase') }}</span>
                </label>
              </div>
              <TimeRangeFilter :initial-range="currentRange" default-preset="24h" @change="onRangeChange" />
              <button
                type="button"
                class="flex items-center gap-1.5 rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-700 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-700"
                :title="t('sensor.console.voltage_chart.close_title')"
                @click="closeFullscreen">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                {{ t('sensor.console.voltage_chart.close') }}
              </button>
            </div>
          </div>
          <div class="flex flex-1 flex-col overflow-auto p-4">
            <SensorChartAlarmCards
              v-model:expanded="alarmsSectionExpanded"
              :alarms="alarmList"
              :is-loading="isAlarmsLoading"
              accent="red"
              @select-alarm="openAlarmDetail"
              @hover-alarm="onAlarmHover" />
            <div ref="chartContainerFullscreen" class="min-h-[28rem] w-full flex-1" />
            <p v-if="isLoading && !hasAnyChartData" class="mt-2 text-center text-sm text-slate-500">
              {{ t('sensor.console.voltage_chart.loading') }}
            </p>
            <p v-else-if="!isLoading && hasTriggeredLoad && !hasAnyChartData" class="mt-2 text-center text-sm text-slate-500">
              {{ t('sensor.console.voltage_chart.no_data') }}
            </p>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import SensorResource from '@/api/resources/sensor'
import SensorChartAlarmCards from '@/components/Sensors/SensorChartAlarmCards.vue'
import type { TimeRangeValue } from '@/components/Globals/TimeRangeFilter'
import { TimeRangeFilter } from '@/components/Globals/TimeRangeFilter'
import type { ISensorChartOverlayAlarm } from '@/interfaces/components/sensorChartAlarm'
import { alarmsToPeriodsMs, fetchSensorChartOverlayAlarms, filterChartOverlayAlarms, resolveChartHoverIndex } from '@/utils/sensor-chart-alarms'
import { buildHoveredAlarmMarkArea } from '@/utils/components/alarm-detail'
import { buildVoltageChartOption, type VoltagePrecariousSetpoint } from '@/utils/components/alarm-detail/voltage-chart-options'
import * as echarts from 'echarts'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useIntersectionObserver, type UseIntersectionObserverReturn } from '@vueuse/core'
import { normalizeVoltageReadingsForChart } from '@/utils/voltage-readings-normalize'
import { resolveVoltagePrecariousSetpoints } from '@/utils/voltage-prodist-setpoints'
import { useI18n } from '@/composable/useI18n'

/** Tipo de alarme relevante para tensão (sobretensão) enviado à API */
const VOLTAGE_ALARM_TYPE = 'overvoltage'

const { t } = useI18n()
const router = useRouter()

const props = defineProps<{
  sensorId: number
  nominalVoltage?: number | null
  /** Indica se o schema do sensor possui chaves de tensão fase-fase (voltage_ab/bc/ca). */
  phasePhaseKeysConfigured?: boolean
}>()

const voltageChartRootRef = ref<HTMLDivElement | null>(null)
const chartContainer = ref<HTMLDivElement | null>(null)
const chartContainerFullscreen = ref<HTMLDivElement | null>(null)
let chartInstance: echarts.ECharts | null = null
let stopObserver: UseIntersectionObserverReturn | null = null
const isFullscreen = ref(false)
/** Seção "Alarmes encontrados" expandida (true) ou recolhida (false) */
const alarmsSectionExpanded = ref(true)
/** Índice do card de alarme sob o mouse (para destacar a faixa no gráfico) */
const hoveredAlarmIndex = ref<number | null>(null)
/** Indica se o fetch já foi disparado (lazy-load ao entrar na viewport) */
const hasTriggeredLoad = ref(false)

const isLoading = ref(false)
const isAlarmsLoading = ref(false)

/** Séries dinâmicas de tensão fase-neutro (F-N): cada uma é [timestamp_ms, value][] */
const seriesVoltageA = ref<[number, number][]>([])
const seriesVoltageB = ref<[number, number][]>([])
const seriesVoltageC = ref<[number, number][]>([])
/** Séries dinâmicas de tensão fase-fase (F-F): cada uma é [timestamp_ms, value][] */
const seriesVoltageAb = ref<[number, number][]>([])
const seriesVoltageBc = ref<[number, number][]>([])
const seriesVoltageCa = ref<[number, number][]>([])
/** Tensões fixas pelo schema (sem série temporal): voltage_a|b|c|ab|bc|ca -> valor em V */
const fixedVoltages = ref<Record<string, number>>({})

const alarmPeriods = ref<[number, number][]>([])
/** Estado do zoom do gráfico (preservado ao maximizar/minimizar) */
const savedDataZoom = ref({ start: 0, end: 100 })
const alarmList = ref<ISensorChartOverlayAlarm[]>([])

const sensorAlarms = computed(() => filterChartOverlayAlarms(alarmList.value))

const PHASE_LABELS: Record<string, string> = {
  voltage_a: 'A',
  voltage_b: 'B',
  voltage_c: 'C',
  voltage_ab: 'AB',
  voltage_bc: 'BC',
  voltage_ca: 'CA',
}

const fixedPhasesLabel = computed(() =>
  Object.keys(fixedVoltages.value)
    .map((k) => PHASE_LABELS[k])
    .filter((label): label is string => Boolean(label))
    .join(', '),
)

const hasSeriesData = computed(
  () =>
    seriesVoltageA.value.length > 0 ||
    seriesVoltageB.value.length > 0 ||
    seriesVoltageC.value.length > 0 ||
    seriesVoltageAb.value.length > 0 ||
    seriesVoltageBc.value.length > 0 ||
    seriesVoltageCa.value.length > 0,
)

const hasFixedVoltages = computed(() => Object.keys(fixedVoltages.value).length > 0)

const hasAnyChartData = computed(() => hasSeriesData.value || hasFixedVoltages.value)

/** Visibilidade das linhas de setpoint por tipo de fase (sempre ao menos 1 ativa). */
const showLinePhase = ref(true)
const showPhasePhase = ref(false)

function toggleLinePhase() {
  if (showLinePhase.value && !showPhasePhase.value) return
  showLinePhase.value = !showLinePhase.value
}

function togglePhasePhase() {
  if (showPhasePhase.value && !showLinePhase.value) return
  showPhasePhase.value = !showPhasePhase.value
}

/** Aviso quando o modo fase-fase está ativo mas o schema não possui as chaves F-F. */
const showPhasePhaseMissingHint = computed(
  () => showPhasePhase.value && props.phasePhaseKeysConfigured === false,
)

/** Setpoints "precarious" do PRODIST conforme a tensão nominal do sensor. */
const precariousSetpoints = computed<VoltagePrecariousSetpoint[]>(() => {
  const resolved = resolveVoltagePrecariousSetpoints(props.nominalVoltage)
  if (!resolved) return []

  const setpoints: VoltagePrecariousSetpoint[] = []
  if (showLinePhase.value) {
    setpoints.push({
      value: resolved.linePhase,
      label: t('sensor.console.voltage_chart.setpoint_line_phase', { value: resolved.linePhase }),
    })
  }
  if (showPhasePhase.value) {
    setpoints.push({
      value: resolved.phasePhase,
      label: t('sensor.console.voltage_chart.setpoint_phase_phase', { value: resolved.phasePhase }),
    })
  }
  return setpoints
})

/** window_period para a API: 5m ou 15m conforme o intervalo */
function windowPeriodMinutes(range: TimeRangeValue): string {
  const hours = (range.end.getTime() - range.start.getTime()) / (60 * 60 * 1000)
  return hours > 48 ? '15m' : '5m'
}

async function fetchAlarmPeriods(range: TimeRangeValue) {
  if (!props.sensorId || !VOLTAGE_ALARM_TYPE) return
  isAlarmsLoading.value = true
  try {
    const list = await fetchSensorChartOverlayAlarms({
      sensorId: props.sensorId,
      sensorAlarmType: VOLTAGE_ALARM_TYPE,
      startDate: range.start.toISOString(),
      endDate: range.end.toISOString(),
    })
    alarmList.value = list
    const sensorOnly = filterChartOverlayAlarms(list)
    alarmPeriods.value = alarmsToPeriodsMs(sensorOnly, range.end.getTime())
  } catch {
    alarmList.value = []
    alarmPeriods.value = []
  } finally {
    isAlarmsLoading.value = false
  }
}

function onAlarmHover(alarm: ISensorChartOverlayAlarm | null) {
  hoveredAlarmIndex.value = resolveChartHoverIndex(alarm, sensorAlarms.value)
}

function openAlarmDetail(alarm: ISensorChartOverlayAlarm) {
  if (alarm.origin === 'group') {
    if (!alarm.group?.id) return
    router.push({
      name: 'group-alarm-detail',
      params: { groupId: String(alarm.group.id), alarmId: String(alarm.id) },
      query: { alarmType: alarm.alarm_type ?? '' },
    })
    return
  }

  router.push({
    name: 'alarm-detail',
    params: {
      sensorId: String(props.sensorId),
      alarmId: String(alarm.id),
    },
    query: { alarmType: alarm.alarm_type ?? '' },
  })
}

function resetSeries() {
  seriesVoltageA.value = []
  seriesVoltageB.value = []
  seriesVoltageC.value = []
  seriesVoltageAb.value = []
  seriesVoltageBc.value = []
  seriesVoltageCa.value = []
  fixedVoltages.value = {}
  savedDataZoom.value = { start: 0, end: 100 }
}

async function fetchReadings(range: TimeRangeValue) {
  if (!props.sensorId) return
  isLoading.value = true
  try {
    const dateRange = `start_date=${encodeURIComponent(range.start.toISOString())}&end_date=${encodeURIComponent(range.end.toISOString())}`
    const resolution = windowPeriodMinutes(range)
    const aggregation = 'mean'

    const [voltageRes] = await Promise.all([
      SensorResource.getEnergyVoltagePowerFactorReadings({
        sensorId: props.sensorId,
        dateRange,
        resolution,
        aggregation,
      }),
      fetchAlarmPeriods(range),
    ])

    const raw = voltageRes?.readings
    const norm = normalizeVoltageReadingsForChart(raw as Record<string, Array<{ t?: string; v?: number }>> | undefined, voltageRes?.fixed_values ?? {})
    seriesVoltageA.value = norm.voltage_a
    seriesVoltageB.value = norm.voltage_b
    seriesVoltageC.value = norm.voltage_c
    seriesVoltageAb.value = norm.voltage_ab
    seriesVoltageBc.value = norm.voltage_bc
    seriesVoltageCa.value = norm.voltage_ca
    fixedVoltages.value = norm.fixed
    savedDataZoom.value = { start: 0, end: 100 }
  } catch {
    resetSeries()
  } finally {
    isLoading.value = false
  }
}

const currentRange = ref<TimeRangeValue | null>(null)

function onRangeChange(range: TimeRangeValue) {
  currentRange.value = range
  if (hasTriggeredLoad.value) fetchReadings(range)
}

/** Extrai start/end do dataZoom da instância atual para preservar ao trocar de container */
function captureDataZoomState() {
  if (!chartInstance) return
  const opt = chartInstance.getOption() as { dataZoom?: Array<{ type?: string; start?: number; end?: number }> } | undefined
  const dz = opt?.dataZoom
  if (Array.isArray(dz) && dz.length > 0) {
    const withRange = dz.find(
      (d) => typeof (d as { start?: number; end?: number }).start === 'number' && typeof (d as { start?: number; end?: number }).end === 'number',
    ) as { start: number; end: number } | undefined
    if (withRange) {
      savedDataZoom.value = { start: withRange.start, end: withRange.end }
    }
  }
}

function openFullscreen() {
  captureDataZoomState()
  const zoomToRestore = { ...savedDataZoom.value }
  if (!hasTriggeredLoad.value) {
    hasTriggeredLoad.value = true
    const defaultRange = currentRange.value ?? null
    if (defaultRange) fetchReadings(defaultRange)
  }
  isFullscreen.value = true
  nextTick(() => {
    nextTick(() => {
      savedDataZoom.value = { ...zoomToRestore }
      initChart()
      nextTick(() => chartInstance?.resize())
    })
  })
}

function closeFullscreen() {
  captureDataZoomState()
  isFullscreen.value = false
  nextTick(() => {
    initChart()
    nextTick(() => chartInstance?.resize())
  })
}

function getActiveChartContainer(): HTMLDivElement | null {
  return isFullscreen.value ? chartContainerFullscreen.value : chartContainer.value
}

function initChart() {
  const container = getActiveChartContainer()
  if (!container) return
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
  chartInstance = echarts.init(container)
  updateChart()
}

function updateChart() {
  if (!chartInstance) return

  const option = buildVoltageChartOption({
    seriesVoltageA: seriesVoltageA.value,
    seriesVoltageB: seriesVoltageB.value,
    seriesVoltageC: seriesVoltageC.value,
    seriesVoltageAb: seriesVoltageAb.value,
    seriesVoltageBc: seriesVoltageBc.value,
    seriesVoltageCa: seriesVoltageCa.value,
    fixedVoltages: fixedVoltages.value,
    precariousSetpoints: precariousSetpoints.value,
    phaseNames: showLinePhase.value
      ? ([
          t('sensor.console.series.voltage_a'),
          t('sensor.console.series.voltage_b'),
          t('sensor.console.series.voltage_c'),
        ] as [string, string, string])
      : null,
    phasePhaseNames: showPhasePhase.value
      ? ([
          t('sensor.console.series.voltage_ab'),
          t('sensor.console.series.voltage_bc'),
          t('sensor.console.series.voltage_ca'),
        ] as [string, string, string])
      : null,
    hoveredMarkArea: buildHoveredAlarmMarkArea(alarmPeriods.value, hoveredAlarmIndex.value),
    dataZoom: savedDataZoom.value,
    sliderBottom: 28,
    showMoveHandle: true,
    accentColor: '#3b82f6',
    fillerColor: 'rgba(59, 130, 246, 0.2)',
  })

  chartInstance.setOption(option)
}

watch(
  [
    seriesVoltageA,
    seriesVoltageB,
    seriesVoltageC,
    seriesVoltageAb,
    seriesVoltageBc,
    seriesVoltageCa,
    fixedVoltages,
    alarmPeriods,
    hoveredAlarmIndex,
    showLinePhase,
    showPhasePhase,
  ],
  () => {
    nextTick(() => updateChart())
  },
  { deep: true },
)

watch(
  () => props.nominalVoltage,
  () => {
    nextTick(() => updateChart())
  },
)

watch(isFullscreen, () => {
  nextTick(() => chartInstance?.resize())
})

const handleResize = () => chartInstance?.resize()

onMounted(() => {
  nextTick(() => initChart())
  stopObserver = useIntersectionObserver(
    voltageChartRootRef,
    ([entry]) => {
      if (!entry.isIntersecting || hasTriggeredLoad.value) return
      hasTriggeredLoad.value = true
      const range = currentRange.value ?? null
      if (range) {
        fetchReadings(range)
      }
    },
    { threshold: 0.01 },
  )

  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  stopObserver?.stop()
  window.removeEventListener('resize', handleResize)
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
